/**
 * Cognara Contract AI Review — exhaustive risk / mitigation / lever analysis.
 * Renders every risk, negotiation lever, missing clause, claims exposure and
 * an executive summary from `POST /api/clm/contracts/{id}/ai-review`.
 * Supports Compare Versions: pick two saved reviews and see the risk delta.
 */
import { useEffect, useState, useCallback, useMemo } from 'react';
import { usePermissions } from '@/contexts/PermissionContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { Sparkles, Loader2, ShieldAlert, Target, FileWarning, Scale, TrendingDown, ClipboardList, History, GitCompareArrows, ArrowRight, Plus, Minus, RefreshCw, MapPin, PlusCircle, FolderOpen, FileText, Gavel, Timer, GitBranch } from 'lucide-react';
import { RiskSourceDialog } from './RiskSourceDialog';
import { ClaimOpportunitiesTable, LdAnalysisPanel, VariationsAnalysisPanel, DisputesAnalysisPanel } from './CognaraClaimPanels';
import { ExplainButton, CognaraReviewProvider } from './CognaraComponentChat';
import api from '@/lib/api';

const API = process.env.REACT_APP_BACKEND_URL;

const sev = (s) => ({
  CRITICAL: { bg: '#EF444422', color: '#F87171' },
  HIGH:     { bg: '#F59E0B22', color: '#FBBF24' },
  MED:      { bg: '#FCD34D22', color: '#FCD34D' },
  P0:       { bg: '#EF444422', color: '#F87171' },
  P1:       { bg: '#F59E0B22', color: '#FBBF24' },
  P2:       { bg: '#22C55E22', color: '#4ADE80' },
})[s] || { bg: '#94A3B822', color: '#CBD5E1' };

const scoreColor = (s) => s >= 75 ? '#22C55E' : s >= 55 ? '#F59E0B' : '#EF4444';

export function CognaraAIReview({ contractId, onIntakeComplete }) {
  const { can } = usePermissions();
  // A review re-extracts clauses and obligations and spends on the engine, so
  // it needs create rights — read-only users get a disabled button instead of a
  // 403 toast after picking documents.
  const canRun = can('CLM', 'create');
  const [loading, setLoading] = useState(false);
  const [review, setReview] = useState(null);
  const [meta, setMeta] = useState(null);
  const [history, setHistory] = useState([]);
  const [attachedFile, setAttachedFile] = useState(null);
  const [mode, setMode] = useState('SINGLE'); // 'SINGLE' | 'COMPARE'
  const [compareAId, setCompareAId] = useState(null);
  const [compareBId, setCompareBId] = useState(null);
  const [progress, setProgress] = useState(null); // { status, step, percent }
  const [sourceRisk, setSourceRisk] = useState(null);
  const [intake, setIntake] = useState(null);   // { clauses, obligations, documents }
  const [contractDocuments, setContractDocuments] = useState([]);
  const [folderTree, setFolderTree] = useState(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [picked, setPicked] = useState([]);
  const [focus, setFocus] = useState('');

  useEffect(() => {
    if (!contractId) return;
    (async () => {
      try {
        const { data } = await api.get(`/contracts/contracts/${contractId}`);
        const docs = data?.documents || [];
        setContractDocuments(docs);
        setPicked(docs.map(d => d.storage_path || d.file_url).filter(Boolean));
      } catch {}
      try {
        const { data } = await api.get(`/contracts/contracts/${contractId}/folders`);
        setFolderTree(data);
      } catch {}
    })();
  }, [contractId]);

  const pickableKeys = useMemo(
    () => contractDocuments.map(d => d.storage_path || d.file_url).filter(Boolean),
    [contractDocuments]);
  const allPicked = pickableKeys.length > 0 && pickableKeys.every(k => picked.includes(k));

  // Documents are picked folder by folder, exactly the way they are filed on the contract.
  const folderGroups = useMemo(() => {
    const known = new Set();
    const groups = (folderTree?.folders || []).map(f => {
      (f.documents || []).forEach(d => known.add(d.storage_path || d.file_url));
      return { folder_id: f.folder_id, name: f.name, documents: f.documents || [] };
    }).filter(g => g.documents.length);
    const loose = contractDocuments.filter(d => !known.has(d.storage_path || d.file_url));
    if (loose.length) groups.push({ folder_id: 'unfiled', name: 'Unfiled', documents: loose });
    return groups.length ? groups : [{ folder_id: 'all', name: 'All documents', documents: contractDocuments }];
  }, [folderTree, contractDocuments]);

  const loadHistory = useCallback(async () => {
    if (!contractId) return;
    try {
      const { data } = await api.get(`/clm/contracts/${contractId}/ai-reviews?limit=20`);
      const list = data.reviews || [];
      setHistory(list);
      if (list.length) { setReview(list[0].review); setMeta(list[0]); setIntake(list[0].intake || null); }
      if (list.length >= 2) {
        setCompareAId(list[1].id);   // previous
        setCompareBId(list[0].id);   // latest
      }
    } catch {}
  }, [contractId]);

  useEffect(() => { loadHistory(); }, [loadHistory]);

  /**
   * Kick off the exhaustive Cognara review as a background job and stream the
   * progress into the local `progress` state.  The endpoint used to be a
   * synchronous Cognara call which routinely exceeded Cloudflare's 100s edge
   * timeout and surfaced as "The origin web server returned an invalid or
   * incomplete response".  The new pattern returns a `job_id` immediately and
   * we poll `/ai-review-status/{job_id}` every 2s until COMPLETED / FAILED —
   * that way risks stream live into the UI instead of appearing all at once.
   */
  const run = async () => {
    if (!contractId) return;
    setPickerOpen(false);
    setLoading(true);
    setProgress({ status: 'QUEUED', step: 'Queuing job…', percent: 5 });
    try {
      const fd = new FormData();
      if (attachedFile) fd.append('file', attachedFile);
      fd.append('picked_docs', JSON.stringify(picked));
      if (focus.trim()) fd.append('focus', focus.trim());
      const r = await api.post(`/clm/contracts/${contractId}/ai-review`, fd);
      const job = r.data;
      const jobId = job?.job_id;
      if (!jobId) throw new Error('No job id returned');

      // Poll every 2s until the background job completes. No cap: a 1,000-page
      // document set is read page by page and that can run for a long time.
      let misses = 0;
      for (;;) {
        await new Promise(res => setTimeout(res, 2000));
        try {
          const { data: st } = await api.get(`/clm/contracts/${contractId}/ai-review-status/${jobId}`);
          misses = 0;
          setProgress({ status: st.status, step: st.step, percent: st.percent });
          if (st.intake) setIntake(st.intake);
          if (st.status === 'COMPLETED') {
            setReview(st.result?.review || null);
            setMeta(st.result || null);
            await loadHistory();
            const cl = st.intake?.clauses ?? 0, ob = st.intake?.obligations ?? 0;
            toast.success(
              `Cognara review v${st.version} generated — ${cl} clause(s) and ${ob} obligation(s) extracted`
            );
            onIntakeComplete?.(st.intake);
            break;
          }
          if (st.status === 'FAILED') {
            throw new Error(st.error || 'Review failed');
          }
        } catch (pollErr) {
          // Transient poll failures shouldn't kill the run — keep trying, but
          // do not loop forever on a job the server no longer knows about.
          if (pollErr?.message === 'No job id returned') throw pollErr;
          if (++misses > 60) throw pollErr;
        }
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || 'AI review failed');
    } finally {
      setLoading(false);
      setTimeout(() => setProgress(null), 2000);
    }
  };

  if (!contractId) return <div className="p-4 text-muted-foreground text-sm">Select a contract to run Cognara review.</div>;

  return (
    <CognaraReviewProvider value={{ contractId, reviewId: meta?.id || null }}>
    <div className="space-y-4" data-testid="cognara-ai-review">
      <Card className="bg-card border-border">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-foreground flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-purple-400" /> Cognara — Ultra-Detailed Contract Audit
          </CardTitle>
          <div className="flex items-center gap-2">
            <div className="flex rounded overflow-hidden border border-border" data-testid="cognara-mode-toggle">
              <button onClick={() => setMode('SINGLE')} className="text-[10px] px-2 py-1 border-r border-border"
                style={{ background: mode === 'SINGLE' ? '#8B5CF6' : 'transparent', color: mode === 'SINGLE' ? '#fff' : 'var(--muted-foreground)' }}
                data-testid="mode-single-btn">Single</button>
              <button onClick={() => setMode('COMPARE')} className="text-[10px] px-2 py-1 flex items-center gap-1"
                style={{ background: mode === 'COMPARE' ? '#8B5CF6' : 'transparent', color: mode === 'COMPARE' ? '#fff' : 'var(--muted-foreground)' }}
                data-testid="mode-compare-btn"
                disabled={history.length < 2}
                title={history.length < 2 ? 'Need at least 2 saved reviews to compare' : ''}>
                <GitCompareArrows className="w-3 h-3" /> Compare
              </button>
            </div>
            <input type="file" accept=".pdf,.docx,.txt,.md" id="cognara-file-input" className="hidden"
              onChange={e => setAttachedFile(e.target.files?.[0] || null)} data-testid="cognara-file-input" />
            <label htmlFor="cognara-file-input" className="text-[10px] cursor-pointer px-2 py-1 rounded border border-border"
              style={{ background: attachedFile ? '#22C55E22' : 'transparent', color: attachedFile ? '#22C55E' : 'var(--muted-foreground)' }}>
              {attachedFile ? `📎 ${attachedFile.name}` : '📎 Attach PDF/DOCX (optional)'}
            </label>
            {meta && mode === 'SINGLE' && <Badge variant="outline" className="text-[10px]">v{meta.version} · {new Date(meta.generated_at).toLocaleString()}</Badge>}
            <Button size="sm" onClick={() => setPickerOpen(true)} disabled={loading || !canRun}
                    title={canRun ? undefined : 'You have read-only access to Contracts & Claims'}
                    className="bg-purple-600 hover:bg-purple-700 text-foreground" data-testid="run-ai-review-btn">
              {loading ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Sparkles className="w-3 h-3 mr-1" />}
              {review ? 'Re-run Review' : 'Run Cognara Review'}
            </Button>
          </div>
        </CardHeader>

        {mode === 'COMPARE' && history.length >= 2 && (
          <CardContent>
            <CompareView history={history} aId={compareAId} bId={compareBId} setAId={setCompareAId} setBId={setCompareBId} />
          </CardContent>
        )}

        {mode === 'SINGLE' && review && (
          <CardContent className="space-y-4">
            {/* Score + verdict + red flags */}
            <div className="grid grid-cols-3 gap-4">
              <div className="col-span-1 rounded-xl p-4 text-center bg-muted border" style={{ borderColor: `${scoreColor(review.cognaraScore)}44` }}>
                <div className="text-xs uppercase tracking-widest text-muted-foreground">Cognara Score</div>
                <div className="text-5xl font-bold my-2" style={{ color: scoreColor(review.cognaraScore) }}>{review.cognaraScore}</div>
                <Badge style={{ background: `${scoreColor(review.cognaraScore)}22`, color: scoreColor(review.cognaraScore) }}>{review.verdict}</Badge>
              </div>
              <div className="col-span-2 rounded-xl p-4" style={{ background: 'transparent', border: '1px solid var(--border)' }}>
                <div className="text-xs uppercase tracking-widest text-muted-foreground mb-2 flex items-center gap-2"><ShieldAlert className="w-3 h-3" /> Top Red Flags</div>
                <div className="space-y-1.5">
                  {(review.topRedFlags || []).map((rf, i) => (
                    <div key={i} className="flex items-start gap-2 text-xs">
                      <Badge style={{ background: sev(rf.severity).bg, color: sev(rf.severity).color }} className="text-[10px] shrink-0">{rf.severity}</Badge>
                      <div className="flex-1"><span className="font-semibold text-foreground">{rf.title}</span> <span className="text-muted-foreground">— {rf.why}</span></div>
                      <ExplainButton kind="redFlag" componentKey={`RF${i + 1}`} label="Red Flag" component={rf} compact />
                    </div>
                  ))}
                </div>
              </div>
            </div>
            {review.executiveSummary && (
              <div className="rounded-xl p-4 text-sm leading-relaxed text-foreground" style={{ background: 'transparent', border: '1px solid var(--border)' }} data-testid="cognara-exec-summary">
                <div className="text-xs uppercase tracking-widest text-muted-foreground mb-2 flex items-center justify-between">
                  Executive Summary
                  <ExplainButton kind="executiveSummary" componentKey="EXEC"
                                 label="Executive Summary"
                                 component={{ title: 'Executive summary', summary: review.executiveSummary, verdict: review.verdict, cognaraScore: review.cognaraScore }} />
                </div>
                {review.executiveSummary}
              </div>
            )}
            {review.risks?.length > 0 && (
              <Section icon={<ShieldAlert className="w-4 h-4" />} title={`Risk Register (${review.risks.length})`} testId="cognara-risks">
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead className="text-muted-foreground uppercase text-[10px]">
                      <tr>{['ID','Title','Cat','L','I','Score','Doc · Clause','Exposure','Mitigation','Owner','Pri',''].map(h => <th key={h} className="text-left px-2 py-1">{h}</th>)}</tr>
                    </thead>
                    <tbody>
                      {review.risks.map(r => {
                        const hasSource = !!(r.documentReference || r.clauseReference || r.quotedText);
                        return (
                        <tr key={r.id} className="border-t border-border" data-testid={`risk-${r.id}`}>
                          <td className="px-2 py-1.5 font-mono text-[10px]">{r.id}</td>
                          <td className="px-2 py-1.5 text-foreground">{r.title}</td>
                          <td className="px-2 py-1.5"><Badge variant="outline" className="text-[9px]">{r.category}</Badge></td>
                          <td className="px-2 py-1.5 text-center">{r.likelihood}</td>
                          <td className="px-2 py-1.5 text-center">{r.impact}</td>
                          <td className="px-2 py-1.5 text-center font-semibold" style={{ color: r.score >= 15 ? '#EF4444' : r.score >= 8 ? '#F59E0B' : '#22C55E' }}>{r.score}</td>
                          <td className="px-2 py-1.5 text-[10px]">
                            {r.documentReference && <div className="text-blue-400 truncate max-w-[140px]" title={r.documentReference}>{r.documentReference}</div>}
                            {r.clauseReference && <div className="text-muted-foreground">Cl. {r.clauseReference}</div>}
                            {!r.documentReference && !r.clauseReference && <span className="text-muted-foreground/70">—</span>}
                          </td>
                          <td className="px-2 py-1.5 text-foreground/80">{r.exposure}</td>
                          <td className="px-2 py-1.5 text-foreground/80">{r.mitigation}</td>
                          <td className="px-2 py-1.5 text-muted-foreground">{r.owner}</td>
                          <td className="px-2 py-1.5"><Badge style={{ background: sev(r.priority).bg, color: sev(r.priority).color }} className="text-[9px]">{r.priority}</Badge></td>
                          <td className="px-2 py-1.5">
                            <div className="flex items-center gap-1">
                              <Button
                                size="sm"
                                variant="ghost"
                                className="h-6 px-2 text-[10px] text-purple-500 hover:text-purple-400 disabled:opacity-30"
                                disabled={!hasSource}
                                onClick={() => setSourceRisk(r)}
                                data-testid={`risk-show-source-${r.id}`}
                                title={hasSource ? 'Open the clause paragraph in the source PDF' : 'No traceability data available'}
                              >
                                <MapPin className="w-3 h-3 mr-1" />Show source
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                className="h-6 px-2 text-[10px] text-emerald-500 hover:text-emerald-400"
                                onClick={async () => {
                                  try {
                                    const res = await api.post(`/contracts/contracts/${contractId}/push-to-raid`, { risk: r });
                                    toast.success(`Pushed to RAID as ${res.data?.raidItems?.[0]?.raidNumber || 'RISK'}`);
                                  } catch (e) {
                                    toast.error(e?.response?.data?.detail || 'Push to RAID failed');
                                  }
                                }}
                                data-testid={`risk-push-raid-${r.id}`}
                                title="Add this risk to the project RAID Log"
                              >
                                <PlusCircle className="w-3 h-3 mr-1" />RAID
                              </Button>
                              <ExplainButton kind="risk" componentKey={r.id} label="Risk" component={r} />
                            </div>
                          </td>
                        </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </Section>
            )}
            {review.negotiationLevers?.length > 0 && (
              <Section icon={<Target className="w-4 h-4" />} title={`Negotiation Levers (${review.negotiationLevers.length})`} testId="cognara-levers">
                <div className="grid gap-2">
                  {review.negotiationLevers.map((l, i) => (
                    <div key={i} className="rounded-lg p-3 text-xs" style={{ background: 'transparent', border: '1px solid var(--border)' }}>
                      <div className="flex items-start justify-between gap-2 mb-1">
                        <div className="font-semibold text-foreground">{l.lever}</div>
                        <ExplainButton kind="lever" componentKey={`LV${i + 1}`} label="Negotiation Lever" component={l} compact />
                      </div>
                      <div className="grid grid-cols-2 gap-2 text-foreground/80">
                        <div><span className="text-muted-foreground/70">Current:</span> {l.currentPosition}</div>
                        <div><span className="text-muted-foreground/70">Target:</span> {l.targetPosition}</div>
                      </div>
                      {l.batna && <div className="mt-1 text-foreground/80"><span className="text-muted-foreground/70">BATNA:</span> {l.batna}</div>}
                      {l.asks?.length > 0 && <ul className="mt-1 list-disc list-inside text-foreground/80">{l.asks.map((a, j) => <li key={j}>{a}</li>)}</ul>}
                    </div>
                  ))}
                </div>
              </Section>
            )}
            {review.missingOrWeakClauses?.length > 0 && (
              <Section icon={<FileWarning className="w-4 h-4" />} title={`Missing / Weak Clauses (${review.missingOrWeakClauses.length})`} testId="cognara-clauses">
                <div className="space-y-1.5">
                  {review.missingOrWeakClauses.map((c, i) => (
                    <div key={i} className="rounded p-2 text-xs" style={{ background: 'transparent', border: '1px solid var(--border)' }}>
                      <span className="font-semibold text-foreground">{c.clause}</span>{' '}
                      <Badge style={{ background: c.status === 'MISSING' ? '#EF444422' : '#F59E0B22', color: c.status === 'MISSING' ? '#F87171' : '#FBBF24' }} className="text-[9px]">{c.status}</Badge>
                      <ExplainButton kind="missingClause" componentKey={`MC${i + 1}`} label="Missing / Weak Clause" component={c} compact />
                      <div className="text-foreground/80 mt-1">{c.recommendedLanguage}</div>
                    </div>
                  ))}
                </div>
              </Section>
            )}
            {review.claimsExposure?.length > 0 && (
              <Section icon={<Scale className="w-4 h-4" />} title={`Claims Exposure (${review.claimsExposure.length})`} testId="cognara-claims">
                <table className="w-full text-xs">
                  <thead className="text-muted-foreground uppercase text-[10px]"><tr>{['Clause','Deadline','Trigger','Est. Value','Recovery %',''].map(h => <th key={h} className="text-left px-2 py-1">{h}</th>)}</tr></thead>
                  <tbody>{review.claimsExposure.map((c, i) => (
                    <tr key={i} className="border-t border-border">
                      <td className="px-2 py-1.5 text-foreground">{c.clause}</td>
                      <td className="px-2 py-1.5">{c.noticeDeadlineDays}</td>
                      <td className="px-2 py-1.5 text-foreground/80">{c.triggerEvent}</td>
                      <td className="px-2 py-1.5 text-foreground/80">{c.estimatedValue}</td>
                      <td className="px-2 py-1.5">{c.recoveryProbabilityPct}</td>
                      <td className="px-2 py-1.5">
                        <ExplainButton kind="claimsExposure" componentKey={`CE${i + 1}`} label="Claim Exposure Item" component={c} compact />
                      </td>
                    </tr>
                  ))}</tbody>
                </table>
              </Section>
            )}
            {review.claimOpportunities?.length > 0 && (
              <Section icon={<Gavel className="w-4 h-4 text-amber-400" />} title={`Claim Opportunities Found in the Documents (${review.claimOpportunities.length})`} testId="cognara-claim-opportunities">
                <ClaimOpportunitiesTable items={review.claimOpportunities} onShowSource={setSourceRisk} />
              </Section>
            )}
            {review.ldAnalysis && (
              <Section icon={<Timer className="w-4 h-4 text-red-400" />} title="Liquidated Damages Analysis" testId="cognara-ld-analysis">
                <LdAnalysisPanel ld={review.ldAnalysis} />
              </Section>
            )}
            {review.variationsAnalysis && (
              <Section icon={<GitBranch className="w-4 h-4 text-violet-400" />} title="Variations Analysis" testId="cognara-variations-analysis">
                <VariationsAnalysisPanel va={review.variationsAnalysis} />
              </Section>
            )}
            {review.disputesAnalysis && (
              <Section icon={<Scale className="w-4 h-4 text-orange-400" />} title="Disputes Analysis" testId="cognara-disputes-analysis">
                <DisputesAnalysisPanel da={review.disputesAnalysis} />
              </Section>
            )}
            {review.cashflowReview && (
              <Section icon={<TrendingDown className="w-4 h-4" />} title="Cashflow & Payment Terms" testId="cognara-cashflow"
                       action={<ExplainButton kind="cashflowReview" componentKey="CASHFLOW" label="Cashflow & Payment Review" component={review.cashflowReview} />}>
                <div className="grid grid-cols-2 gap-2 text-xs text-foreground/80">
                  {Object.entries(review.cashflowReview).map(([k, v]) => (
                    <div key={k} className="rounded p-2" style={{ background: 'transparent', border: '1px solid var(--border)' }}>
                      <div className="text-muted-foreground/70 text-[10px] uppercase">{k}</div><div>{v}</div>
                    </div>
                  ))}
                </div>
              </Section>
            )}
            {review.complianceFit && (
              <Section icon={<ClipboardList className="w-4 h-4" />} title={`Compliance Fit — ${review.complianceFit.framework} (${review.complianceFit.score}/100)`} testId="cognara-compliance"
                       action={<ExplainButton kind="complianceFit" componentKey="COMPLIANCE" label="Compliance Fit" component={review.complianceFit} />}>
                <ul className="list-disc list-inside text-xs text-foreground/80 space-y-1">
                  {(review.complianceFit.gaps || []).map((g, i) => <li key={i}>{typeof g === 'string' ? g : JSON.stringify(g)}</li>)}
                </ul>
              </Section>
            )}
            {review.recommendedActions?.length > 0 && (
              <Section icon={<ClipboardList className="w-4 h-4" />} title={`Recommended Actions (${review.recommendedActions.length})`} testId="cognara-actions">
                <table className="w-full text-xs">
                  <thead className="text-muted-foreground uppercase text-[10px]"><tr>{['Action','Priority','Due','Owner',''].map(h => <th key={h} className="text-left px-2 py-1">{h}</th>)}</tr></thead>
                  <tbody>{review.recommendedActions.map((a, i) => (
                    <tr key={i} className="border-t border-border">
                      <td className="px-2 py-1.5 text-foreground">{a.action}</td>
                      <td className="px-2 py-1.5"><Badge style={{ background: sev(a.priority).bg, color: sev(a.priority).color }} className="text-[9px]">{a.priority}</Badge></td>
                      <td className="px-2 py-1.5">{a.dueDays}d</td>
                      <td className="px-2 py-1.5 text-muted-foreground">{a.owner}</td>
                      <td className="px-2 py-1.5">
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-6 px-2 text-[10px] text-emerald-500 hover:text-emerald-400"
                          onClick={async () => {
                            try {
                              const res = await api.post(`/contracts/contracts/${contractId}/push-to-raid`, { action: a });
                              toast.success(`Pushed to RAID as ${res.data?.raidItems?.[0]?.raidNumber || 'ACTION'}`);
                            } catch (e) {
                              toast.error(e?.response?.data?.detail || 'Push to RAID failed');
                            }
                          }}
                          data-testid={`action-push-raid-${i}`}
                          title="Add this recommended action to the project RAID Log"
                        >
                          <PlusCircle className="w-3 h-3 mr-1" />RAID
                        </Button>
                        <ExplainButton kind="recommendedAction" componentKey={`RA${i + 1}`} label="Recommended Action" component={a} compact />
                      </td>
                    </tr>
                  ))}</tbody>
                </table>
              </Section>
            )}
            {history.length > 1 && (
              <Section icon={<History className="w-4 h-4" />} title={`Version History (${history.length})`}>
                <div className="text-xs text-foreground/80">
                  {history.map(h => (
                    <button key={h.id} onClick={() => { setReview(h.review); setMeta(h); }} className="mr-2 mb-1 px-2 py-1 rounded border border-border" style={{ background: meta?.id === h.id ? 'rgba(139,92,246,0.25)' : 'transparent' }}>
                      v{h.version} · {new Date(h.generated_at).toLocaleDateString()}
                    </button>
                  ))}
                </div>
              </Section>
            )}
          </CardContent>
        )}
        {mode === 'SINGLE' && !review && !loading && (
          <CardContent><div className="text-muted-foreground text-sm py-8 text-center">Click "Run Cognara Review" to get an exhaustive audit of every risk, missing clause, negotiation lever and claims exposure.</div></CardContent>
        )}

        {/* Live progress bar — streams into the same card so users see risks arrive */}
        {loading && progress && (
          <CardContent data-testid="cognara-progress">
            <div className="rounded-lg p-3" style={{ background: 'transparent', border: '1px solid var(--border)' }}>
              <div className="flex items-center justify-between text-xs mb-1.5">
                <span className="text-foreground flex items-center gap-2"><Loader2 className="w-3 h-3 animate-spin text-purple-400" />{progress.step || 'Working…'}</span>
                <span className="text-purple-300 font-semibold">{Math.round(progress.percent || 0)}%</span>
              </div>
              <Progress value={progress.percent || 0} className="h-1.5" />
              <p className="mt-2 text-[10px] text-muted-foreground/70">Re-run Review reads every uploaded document end-to-end, extracts all clauses &amp; obligations, then runs the risk audit — usually 90–240 seconds.</p>
            </div>
          </CardContent>
        )}

        {/* Intake result — what the last run pulled out of the documents */}
        {!loading && intake && (
          <CardContent data-testid="cognara-intake-summary" className="pt-0">
            <div className="rounded-lg px-3 py-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px]"
              style={{ background: 'transparent', border: '1px solid var(--border)' }}>
              <span className="uppercase tracking-widest text-muted-foreground">Document intake</span>
              <span className="text-foreground" data-testid="intake-clause-count"><b>{intake.clauses ?? 0}</b> clauses</span>
              <span className="text-foreground" data-testid="intake-obligation-count"><b>{intake.obligations ?? 0}</b> obligations</span>
              {!!intake.chunks && <span className="text-muted-foreground">{intake.chunks} slice(s) read</span>}
              {!!intake.documents_read?.length && (
                <span className="text-muted-foreground truncate max-w-[520px]">
                  read {intake.documents_read.join(', ')}
                </span>
              )}
              {!intake.documents_read?.length && !!intake.documents?.length && (
                <span className="text-muted-foreground truncate max-w-[520px]">from {intake.documents.join(', ')}</span>
              )}
              {intake.skipped && <span className="text-amber-400">{intake.skipped}</span>}
            </div>
            {!!intake.unreadable?.length && (
              <div className="mt-2 rounded-lg px-3 py-2 text-[11px]"
                   style={{ border: '1px solid var(--border)' }}
                   data-testid="intake-unreadable">
                <div className="uppercase tracking-widest text-amber-400 mb-1">
                  {intake.unreadable.length} document(s) could not be read
                </div>
                <ul className="space-y-0.5 text-muted-foreground">
                  {intake.unreadable.map((u, i) => (
                    <li key={i} className="truncate" data-testid={`intake-unreadable-${i}`}>
                      <span className="text-foreground">{u.file_name}</span> — {u.reason}
                    </li>
                  ))}
                </ul>
                <div className="mt-1 text-muted-foreground">
                  Re-supply these as a text PDF or Office file — Cognara could not take any terms from them.
                </div>
              </div>
            )}
            {!!intake.vision_pages?.length && (
              <div className="mt-2 rounded-lg px-3 py-2 text-[11px]"
                   style={{ border: '1px solid var(--border)' }}
                   data-testid="intake-vision-pages">
                <div className="uppercase tracking-widest text-emerald-400 mb-1">
                  Pages read directly from the page image
                </div>
                <ul className="space-y-0.5 text-muted-foreground">
                  {intake.vision_pages.map((v, i) => (
                    <li key={i} className="truncate" data-testid={`intake-vision-page-${i}`}>
                      <span className="text-foreground">{v.document}</span> — page{' '}
                      {(v.pages || []).join(', ')} ({(v.reasons || []).join('; ')})
                    </li>
                  ))}
                </ul>
                <div className="mt-1 text-muted-foreground">
                  OCR was unreliable on these pages, so Cognara opened them as images and read
                  them directly. Figures quoted from them come from the page images.
                </div>
              </div>
            )}
          </CardContent>
        )}
      </Card>

      {/* Document picker — every run starts by choosing documents, folder by folder */}
      <Dialog open={pickerOpen} onOpenChange={setPickerOpen}>
        <DialogContent className="max-w-2xl w-[95vw] max-h-[88vh] overflow-hidden flex flex-col"
                       data-testid="cognara-review-picker">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base">
              <Sparkles className="w-4 h-4 text-purple-400" />
              Which documents should Cognara read?
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 flex-1 min-h-0 min-w-0 flex flex-col">
            <div className="flex items-start justify-between gap-3">
              <span className="text-[11px] text-muted-foreground min-w-0 flex-1">
                {picked.length} of {pickableKeys.length} selected · Cognara will extract clauses,
                obligations, claims &amp; claim opportunities, LD, variations and disputes from these.
              </span>
              <button className="text-[10px] text-sky-500 shrink-0 whitespace-nowrap"
                      onClick={() => setPicked(allPicked ? [] : pickableKeys)}
                      data-testid="review-picker-toggle-all">
                {allPicked ? 'Clear all' : 'Select all'}
              </button>
            </div>
            {!contractDocuments.length ? (
              <p className="text-xs text-muted-foreground border border-dashed rounded-lg p-4 text-center"
                 data-testid="review-picker-empty">
                No documents on this contract yet — upload them from the contract header first.
              </p>
            ) : (
              <ScrollArea className="h-[45vh] min-h-[14rem] border rounded-lg">
                <div className="p-2 space-y-2 min-w-0">
                  {folderGroups.map((g) => {
                    const keys = g.documents.map(d => d.storage_path || d.file_url).filter(Boolean);
                    const all = keys.length > 0 && keys.every(k => picked.includes(k));
                    return (
                      <div key={g.folder_id} data-testid={`review-folder-${g.folder_id}`}>
                        <div className="flex items-center gap-2 px-1 py-0.5 bg-muted/60 rounded min-w-0">
                          <Checkbox checked={all} disabled={!keys.length}
                                    onCheckedChange={(v) => setPicked(p => v
                                      ? Array.from(new Set([...p, ...keys]))
                                      : p.filter(x => !keys.includes(x)))}
                                    data-testid={`review-folder-check-${g.folder_id}`} />
                          <FolderOpen className="w-3 h-3 text-orange-500 shrink-0" />
                          <span className="text-[11px] font-medium truncate min-w-0">{g.name}</span>
                          <span className="ml-auto text-[10px] text-muted-foreground shrink-0">
                            {keys.filter(k => picked.includes(k)).length}/{g.documents.length}
                          </span>
                        </div>
                        {g.documents.map((d, i) => {
                          const key = d.storage_path || d.file_url;
                          return (
                            <label key={key || i}
                                   className="flex items-center gap-2 text-xs pl-6 pr-2 py-1 rounded hover:bg-muted/50 cursor-pointer min-w-0">
                              <Checkbox checked={picked.includes(key)}
                                        onCheckedChange={(v) => setPicked(p => v
                                          ? [...p, key] : p.filter(x => x !== key))}
                                        data-testid={`review-doc-${contractDocuments.findIndex(x => (x.storage_path || x.file_url) === key)}`} />
                              <FileText className="w-3 h-3 text-muted-foreground shrink-0" />
                              <span className="truncate min-w-0 flex-1">{d.file_name || key}</span>
                            </label>
                          );
                        })}
                      </div>
                    );
                  })}
                </div>
              </ScrollArea>
            )}
            <Textarea rows={2} value={focus} onChange={e => setFocus(e.target.value)}
                      placeholder="Anything specific to focus on? e.g. late access to Area 300, the notice served on 14 March"
                      className="text-xs shrink-0 resize-none" data-testid="review-picker-focus" />
          </div>
          <DialogFooter className="shrink-0">
            <Button variant="ghost" size="sm" onClick={() => setPickerOpen(false)} data-testid="review-picker-cancel">Cancel</Button>
            <Button size="sm" onClick={run} disabled={!picked.length}
                    className="bg-purple-600 hover:bg-purple-700" data-testid="review-picker-run">
              <Sparkles className="w-3 h-3 mr-1" />Run Cognara on {picked.length} document(s)
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Show-source inline PDF drilldown for every risk row */}
      <RiskSourceDialog
        open={!!sourceRisk}
        onOpenChange={(o) => !o && setSourceRisk(null)}
        risk={sourceRisk}
        contractId={contractId}
        documents={contractDocuments}
      />
    </div>
    </CognaraReviewProvider>
  );
}

function Section({ icon, title, testId, action, children }) {
  return (
    <div data-testid={testId}>
      <div className="text-xs uppercase tracking-widest text-muted-foreground mb-2 flex items-center gap-2">
        {icon} {title}
        {action && <span className="ml-auto">{action}</span>}
      </div>
      {children}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Compare Versions view — shows risk / score / verdict delta
// ─────────────────────────────────────────────────────────────
function CompareView({ history, aId, bId, setAId, setBId }) {
  const a = useMemo(() => history.find(h => h.id === aId), [history, aId]);
  const b = useMemo(() => history.find(h => h.id === bId), [history, bId]);

  const delta = useMemo(() => {
    if (!a?.review || !b?.review) return null;
    const rA = a.review, rB = b.review;
    const risksA = new Map((rA.risks || []).map(r => [r.id || r.title, r]));
    const risksB = new Map((rB.risks || []).map(r => [r.id || r.title, r]));
    const added = [], removed = [], changed = [];
    for (const [k, r] of risksB) {
      const other = risksA.get(k);
      if (!other) added.push(r);
      else if ((other.score || 0) !== (r.score || 0) || (other.priority || '') !== (r.priority || '')) changed.push({ from: other, to: r });
    }
    for (const [k, r] of risksA) if (!risksB.has(k)) removed.push(r);
    return {
      scoreDelta: (rB.cognaraScore || 0) - (rA.cognaraScore || 0),
      verdictChange: rA.verdict !== rB.verdict,
      risksCountDelta: (rB.risks?.length || 0) - (rA.risks?.length || 0),
      added, removed, changed,
    };
  }, [a, b]);

  if (!a || !b) return <div className="text-muted-foreground text-sm py-4">Pick two review versions to compare.</div>;

  return (
    <div className="space-y-4" data-testid="cognara-compare-view">
      {/* Version selectors */}
      <div className="grid grid-cols-[1fr_auto_1fr] items-end gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">Version A (baseline)</div>
          <Select value={aId || ''} onValueChange={setAId}>
            <SelectTrigger className="h-8 text-xs bg-muted border-border" data-testid="compare-a-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {history.map(h => <SelectItem key={h.id} value={h.id}>v{h.version} · {new Date(h.generated_at).toLocaleString()}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <ArrowRight className="w-5 h-5 text-purple-400 mb-2" />
        <div>
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">Version B (compare)</div>
          <Select value={bId || ''} onValueChange={setBId}>
            <SelectTrigger className="h-8 text-xs bg-muted border-border" data-testid="compare-b-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {history.map(h => <SelectItem key={h.id} value={h.id}>v{h.version} · {new Date(h.generated_at).toLocaleString()}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Headline deltas */}
      {delta && (
        <div className="grid grid-cols-4 gap-3" data-testid="compare-headline">
          <DeltaCard label="Score Δ" value={`${delta.scoreDelta > 0 ? '+' : ''}${delta.scoreDelta}`}
            color={delta.scoreDelta > 0 ? '#22C55E' : delta.scoreDelta < 0 ? '#EF4444' : '#94A3B8'}
            sub={`${a.review.cognaraScore} → ${b.review.cognaraScore}`} />
          <DeltaCard label="Verdict" value={b.review.verdict || '—'}
            color={scoreColor(b.review.cognaraScore || 0)}
            sub={delta.verdictChange ? `Changed from ${a.review.verdict}` : 'No change'} />
          <DeltaCard label="Risks Δ" value={`${delta.risksCountDelta > 0 ? '+' : ''}${delta.risksCountDelta}`}
            color={delta.risksCountDelta > 0 ? '#EF4444' : delta.risksCountDelta < 0 ? '#22C55E' : '#94A3B8'}
            sub={`${a.review.risks?.length || 0} → ${b.review.risks?.length || 0}`} />
          <DeltaCard label="New / Removed / Changed" value={`${delta.added.length} / ${delta.removed.length} / ${delta.changed.length}`}
            color="#8B5CF6" sub="risks between versions" />
        </div>
      )}

      {/* Risk delta tables */}
      {delta && (
        <>
          {delta.added.length > 0 && (
            <DeltaSection icon={<Plus className="w-3.5 h-3.5" />} title={`New Risks in v${b.version} (${delta.added.length})`} color="#EF4444" testId="delta-added">
              <RiskTable risks={delta.added} />
            </DeltaSection>
          )}
          {delta.removed.length > 0 && (
            <DeltaSection icon={<Minus className="w-3.5 h-3.5" />} title={`Resolved / Removed since v${a.version} (${delta.removed.length})`} color="#22C55E" testId="delta-removed">
              <RiskTable risks={delta.removed} />
            </DeltaSection>
          )}
          {delta.changed.length > 0 && (
            <DeltaSection icon={<RefreshCw className="w-3.5 h-3.5" />} title={`Changed Risks (${delta.changed.length})`} color="#F59E0B" testId="delta-changed">
              <table className="w-full text-xs">
                <thead className="text-muted-foreground uppercase text-[10px]"><tr>{['ID','Title','Score A → B','Priority A → B'].map(h => <th key={h} className="text-left px-2 py-1">{h}</th>)}</tr></thead>
                <tbody>
                  {delta.changed.map((c, i) => (
                    <tr key={i} className="border-t border-border">
                      <td className="px-2 py-1.5 font-mono text-[10px]">{c.to.id}</td>
                      <td className="px-2 py-1.5 text-foreground">{c.to.title}</td>
                      <td className="px-2 py-1.5">{c.from.score} → <span className="font-semibold" style={{ color: c.to.score > c.from.score ? '#EF4444' : '#22C55E' }}>{c.to.score}</span></td>
                      <td className="px-2 py-1.5">{c.from.priority} → <Badge style={{ background: sev(c.to.priority).bg, color: sev(c.to.priority).color }} className="text-[9px]">{c.to.priority}</Badge></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </DeltaSection>
          )}
          {delta.added.length === 0 && delta.removed.length === 0 && delta.changed.length === 0 && (
            <div className="rounded-xl p-6 text-center text-sm text-muted-foreground" style={{ background: 'transparent', border: '1px solid var(--border)' }}>
              Both versions have an identical risk register — no material change detected.
            </div>
          )}
        </>
      )}
    </div>
  );
}

function DeltaCard({ label, value, color, sub }) {
  return (
    <div className="rounded-xl p-3 text-center bg-muted border" style={{ borderColor: `${color}44` }}>
      <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{label}</div>
      <div className="text-2xl font-bold my-1" style={{ color }}>{value}</div>
      <div className="text-[10px] text-muted-foreground/70">{sub}</div>
    </div>
  );
}

function DeltaSection({ icon, title, color, testId, children }) {
  return (
    <div data-testid={testId}>
      <div className="text-xs uppercase tracking-widest mb-2 flex items-center gap-2" style={{ color }}>{icon} {title}</div>
      <div className="overflow-x-auto rounded-lg" style={{ background: 'transparent', border: '1px solid var(--border)' }}>{children}</div>
    </div>
  );
}

function RiskTable({ risks }) {
  return (
    <table className="w-full text-xs">
      <thead className="text-muted-foreground uppercase text-[10px]">
        <tr>{['ID','Title','Cat','Score','Exposure','Priority'].map(h => <th key={h} className="text-left px-2 py-1">{h}</th>)}</tr>
      </thead>
      <tbody>
        {risks.map((r, i) => (
          <tr key={r.id || i} className="border-t border-border">
            <td className="px-2 py-1.5 font-mono text-[10px]">{r.id}</td>
            <td className="px-2 py-1.5 text-foreground">{r.title}</td>
            <td className="px-2 py-1.5"><Badge variant="outline" className="text-[9px]">{r.category}</Badge></td>
            <td className="px-2 py-1.5 font-semibold" style={{ color: r.score >= 15 ? '#EF4444' : r.score >= 8 ? '#F59E0B' : '#22C55E' }}>{r.score}</td>
            <td className="px-2 py-1.5 text-foreground/80">{r.exposure}</td>
            <td className="px-2 py-1.5"><Badge style={{ background: sev(r.priority).bg, color: sev(r.priority).color }} className="text-[9px]">{r.priority}</Badge></td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export default CognaraAIReview;
